import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Wallet, Banknote, Smartphone, Globe, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner } from "../../components/common";
import { FetchButton, FetchForm, FetchPrompt, useFetchGate } from "../../components/FetchBar";
import { StaffName } from "../../components/StaffAvatar";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { api, errMsg, fmtINR } from "../../lib/api";
import { useLiveCollections } from "../../lib/liveCollections";

const today = () => new Date().toISOString().slice(0, 10);

const KPI = [
  { key: "cash", label: "Cash in hand", icon: Wallet },
  { key: "cheque", label: "Cheques", icon: Banknote },
  { key: "pos", label: "PoS device", icon: Smartphone },
  { key: "online", label: "Online / gateway", icon: Globe },
];

export default function DayReport() {
  const { code } = useParams();
  const [date, setDate] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Phase 133: the reconciliation loads only on Fetch, for the date applied at that moment
  const gate = useFetchGate({ date });
  const appliedDate = gate.applied?.date;

  const load = useCallback(() => {
    if (!appliedDate) return;
    setLoading(true);
    api.get(`/t/${code}/reports/daily-collections`, { params: { date: appliedDate } })
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(errMsg(e)); setData({ collectors: [], totals: {} }); })
      .finally(() => setLoading(false));
  }, [code, appliedDate]);
  const live = useLiveCollections(code);   // Phase 149
  useEffect(() => { if (gate.fetched) load(); }, [load, gate.fetched, gate.version, live]);

  const t = data?.totals || {};

  return (
    <div className="space-y-4">
      <PageHeader title="Day Collections" subtitle="End-of-day reconciliation — cash, cheque, PoS and online collections per collector">
        <FetchForm onFetch={gate.fetch} className="flex items-end gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Date</Label>
            <Input type="date" className="h-10 w-40" value={date} max={today()}
              onChange={(e) => setDate(e.target.value)} data-testid="day-report-date-input" />
          </div>
          <FetchButton type="submit" loading={loading} dirty={gate.dirty} fetched={gate.fetched} disabled={!date} testId="day-report-fetch-button" />
        </FetchForm>
      </PageHeader>

      {!gate.fetched ? (
        <Card className="shadow-sm"><CardContent className="p-0">
          <FetchPrompt onFetch={gate.fetch} testId="day-report-fetch-prompt"
            subtitle="Pick the day above, then click Fetch to reconcile cash, cheque, PoS and online collections per collector." />
        </CardContent></Card>
      ) : !data ? <Spinner /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            {KPI.map((k) => (
              <Card key={k.key} className="shadow-sm" data-testid={`day-kpi-${k.key}`}>
                <CardContent className="p-4 flex items-center gap-3">
                  <span className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <k.icon className="h-4.5 w-4.5" />
                  </span>
                  <div>
                    <p className="text-lg font-semibold tabular-nums leading-tight">{fmtINR(t[k.key] || 0)}</p>
                    <p className="text-xs text-muted-foreground">{k.label}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
            <Card className="shadow-sm border-primary/30" data-testid="day-kpi-total">
              <CardContent className="p-4">
                <p className="text-lg font-semibold tabular-nums leading-tight text-primary">{fmtINR(t.total || 0)}</p>
                <p className="text-xs text-muted-foreground">Total collected • {t.count || 0} receipts</p>
              </CardContent>
            </Card>
          </div>

          {(t.cheque_pending > 0 || t.bounced > 0) && (
            <div className="flex flex-wrap gap-3 text-xs">
              {t.cheque_pending > 0 && (
                <span className="rounded-full border border-amber-200 bg-amber-50 text-amber-800 px-3 py-1.5" data-testid="day-cheque-pending-note">
                  {fmtINR(t.cheque_pending)} in cheques awaiting bank credit
                </span>
              )}
              {t.bounced > 0 && (
                <span className="rounded-full border border-red-200 bg-red-50 text-red-800 px-3 py-1.5 inline-flex items-center gap-1" data-testid="day-bounced-note">
                  <AlertTriangle className="h-3.5 w-3.5" /> {fmtINR(t.bounced)} reversed (bounced cheques)
                </span>
              )}
            </div>
          )}

          {data.collectors.length === 0 ? (
            <Card className="shadow-sm">
              <CardContent className="p-10 text-center text-sm text-muted-foreground" data-testid="day-report-empty-state">
                No collections recorded on {date}. Pick another date or check back after field activity.
              </CardContent>
            </Card>
          ) : (
            <Card className="shadow-sm">
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Collector</TableHead>
                      <TableHead className="text-xs text-right">Cash</TableHead>
                      <TableHead className="text-xs text-right">Cheque</TableHead>
                      <TableHead className="text-xs text-right">PoS</TableHead>
                      <TableHead className="text-xs text-right">Online</TableHead>
                      <TableHead className="text-xs text-right">Other</TableHead>
                      <TableHead className="text-xs text-right">Receipts</TableHead>
                      <TableHead className="text-xs text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.collectors.map((r) => (
                      <TableRow key={r.collector_id || "online"} data-testid="day-report-row">
                        <TableCell className="text-xs font-medium">
                          {r.collector_id ? <StaffName code={code} userId={r.collector_id} name={r.collector_name} size="xs" nameClassName="text-xs" /> : (r.collector_name || "—")}
                          {r.bounced > 0 && <span className="block text-[10px] text-red-700">−{fmtINR(r.bounced)} bounced</span>}
                          {r.cheque_pending > 0 && <span className="block text-[10px] text-amber-700">{fmtINR(r.cheque_pending)} cheque pending credit</span>}
                        </TableCell>
                        <TableCell className="text-xs text-right tabular-nums">{r.cash ? fmtINR(r.cash) : "—"}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums">{r.cheque ? fmtINR(r.cheque) : "—"}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums">{r.pos ? fmtINR(r.pos) : "—"}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums">{r.online ? fmtINR(r.online) : "—"}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums">{r.other ? fmtINR(r.other) : "—"}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums">{r.count}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums font-semibold">{fmtINR(r.total)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-muted/60 font-medium" data-testid="day-report-totals-row">
                      <TableCell className="text-xs">Total</TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{fmtINR(t.cash || 0)}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{fmtINR(t.cheque || 0)}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{fmtINR(t.pos || 0)}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{fmtINR(t.online || 0)}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{fmtINR(t.other || 0)}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{t.count || 0}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums font-semibold">{fmtINR(t.total || 0)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
